/**
 * MerchantSelector.tsx
 *
 * Matches legacy payment-details-merchselect.php + payment-details-typeselect.php.
 * Auto-selects if only one merchant; shows dropdown if multiple.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listMerchants, type Merchant } from '@/services/payments.service';
import { Spinner } from '@/components/ui';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';

interface MerchantSelectorProps {
  value: number | null;
  onChange: (merchantId: number, merchant: Merchant) => void;
  disabled?: boolean;
  /** Explicit facility override from the caller (e.g. appointment facility). Takes priority over the global store. */
  preferredFacilityId?: number | null;
}

export function MerchantSelector({ value, onChange, disabled, preferredFacilityId }: MerchantSelectorProps) {
  const { data: merchants, isLoading } = useQuery({
    queryKey: ['payment-merchants'],
    queryFn: listMerchants,
    staleTime: 5 * 60 * 1000,
  });

  const overrideFacilityId = useGlobalFacilityStore((s) => s.selectedFacilityIds[0] ?? null);
  const effectiveFacilityId = preferredFacilityId ?? overrideFacilityId;

  const items = merchants ?? [];

  const autoPick = useMemo(() => {
    if (items.length === 0) return null;
    // Single merchant is unambiguous — always auto-select it, even without a
    // facility context, so the card-entry form can mount.
    if (items.length === 1) return items[0];
    if (effectiveFacilityId == null) return null;
    const facilityMatch = items.find((m) => m.facilityId === effectiveFacilityId);
    return facilityMatch ?? items.find((m) => m.isDefault) ?? items[0] ?? null;
  }, [items, effectiveFacilityId]);

  // Deliberately no dependency array. Host modals reset their merchant state in
  // an `open` effect, and React flushes child effects *before* the parent's, so
  // a dep-guarded auto-select fires once, gets clobbered back to null, and then
  // never re-fires (no dep actually changed). Re-asserting the pick each render
  // is cheap: we bail the moment `value` is set, and a repeat onChange with the
  // same merchant is a no-op setState. Without this, an account with a single
  // merchant shows the merchant name but leaves the caller's merchantId null,
  // which fails validation at pay time.
  useEffect(() => {
    if (value || disabled || !autoPick) return;
    onChange(autoPick.merchantId, autoPick);
  });

  if (isLoading) return <Spinner />;

  if (items.length <= 1) {
    const single = items[0];
    return (
      <div className="text-sm text-slate-600">
        <span className="font-medium">Merchant:</span>{' '}
        {single ? single.friendlyTitle : 'No merchants configured'}
      </div>
    );
  }

  return (
    <label className="space-y-1 text-sm text-slate-700">
      <span className="font-medium">Merchant</span>
      <select
        value={value ?? ''}
        onChange={(e) => {
          const id = Number(e.target.value);
          const m = items.find((x) => x.merchantId === id);
          if (m) onChange(id, m);
        }}
        disabled={disabled}
        className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="">Select merchant...</option>
        {items.map((m) => (
          <option key={m.merchantId} value={m.merchantId}>
            {m.friendlyTitle}{m.isDefault ? ' (Default)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}
